import PropTypes from 'prop-types';

/**
 * The buildings on a world, and whether each is open right now.
 *
 * EVERY BUILDING, not every one with hours. A building nobody has authored is
 * the normal case and the one you are most likely looking for, so an empty list
 * here means an empty world rather than "nothing configured yet".
 *
 * THE BADGE IS THE SERVER'S ANSWER, computed in game/businessHours.js and sent
 * as `openNow`. Recomputing it in the browser would be a second copy of the
 * rules — and the copy is the one that drifts, which would show a shop as open
 * on this page while the world drew it shut.
 */

const BADGE = {
  open: { text: 'Open', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  hours: { text: 'Closed', className: 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  temporary: { text: 'Closed for now', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' },
  permanent: { text: 'Closed down', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  soon: { text: 'Coming soon', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
};

/** Whether anything has been authored, which is not the same as being shut. */
const isAuthored = (b) => b.hours.length > 0 || b.closure !== 'none';

function BusinessList({ businesses, selectedId, onSelect }) {
  if (businesses.length === 0) {
    return (
      <p className="rounded border border-dashed border-gray-300 p-3 text-xs text-gray-500 dark:border-gray-700">
        This world has no buildings yet. Draw one in the world editor first —
        hours are set per building, and there is nothing here to set them on.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-gray-200 rounded border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
      {businesses.map((b) => {
        const badge = BADGE[b.openNow ? 'open' : b.reason] || BADGE.hours;

        return (
          <li key={b.zoneId}>
            <button
              type="button"
              onClick={() => onSelect(b.zoneId)}
              className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                b.zoneId === selectedId
                  ? 'bg-blue-50 dark:bg-blue-950'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-900'
              }`}
            >
              <span className="min-w-0">
                <span className="block truncate text-gray-900 dark:text-gray-100">
                  {/* An unnamed building draws nothing in the world, so hours on
                      it would be invisible. Say so here rather than let somebody
                      author a schedule nobody can ever see. */}
                  {b.label || <span className="italic text-gray-500">unnamed</span>}
                </span>
                <span className="block text-xs text-gray-500">
                  {b.kind}
                  {isAuthored(b) ? '' : ' · nothing set'}
                </span>
              </span>

              <span className={`shrink-0 rounded px-2 py-0.5 text-xs ${badge.className}`}>
                {badge.text}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

BusinessList.propTypes = {
  businesses: PropTypes.arrayOf(PropTypes.shape({
    zoneId: PropTypes.string.isRequired,
    label: PropTypes.string,
    kind: PropTypes.string,
    openNow: PropTypes.bool,
    reason: PropTypes.string,
  })).isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

BusinessList.defaultProps = { selectedId: null };

export default BusinessList;
